import relationship from "../assets/images/relationship-healing-about.svg";
import therapy from "../assets/images/therapy-icon.jpg";
const tarot = "/images/tarothero.webp";
const reiki = "/images/rhbg.webp";
const ritual = "/images/sbg.webp";
const temple = "/images/Stemple.webp";
const hanuman = "/images/Hanuman.webp";
const kali = "/images/mahakali-ritual.webp";
const kamakhya = "/images/maa-kamakhya-healing-hero.webp";
const numbers = "/images/numberhero.webp";
const oils = "/images/oilshero.webp";
const cards = "/images/tbg.webp";

export const whatsapp = (
  message = "Hello, I would like help choosing a Radian Soul session.",
) => `https://wa.me/917375015958?text=${encodeURIComponent(message)}`;
export type Category =
  "All services" | "Guidance" | "Healing" | "Counselling" | "Courses";
export interface Plan {
  title: string;
  duration: string;
  price: number;
  group?: string;
  note?: string;
}
export interface Service {
  slug: string;
  title: string;
  category: Category;
  image: string;
  imageAlt: string;
  intro: string;
  description: string;
  focus: string[];
  format: string;
  plans: Plan[];
  note?: string;
  curriculum?: string[];
  comingSoon?: boolean;
  paymentName?: string;
}
const sacredPlans = (title: string): Plan[] => [
  { title: "Single Session", duration: "30 minutes", price: 699 },
  { title, duration: "5 sessions", price: 3200 },
];
export const services: Service[] = [
  {
    slug: "tarot",
    title: "Tarot Reading",
    category: "Guidance",
    image: tarot,
    imageAlt: "Tarot cards laid out beside candles during a reading",
    intro: "Make room for a different perspective.",
    description:
      "Bring the question that has been on your mind. Explore relationships, work, or a personal decision through a focused tarot reading with Vaanii.",
    focus: [
      "Relationships & connection",
      "Career & decisions",
      "Personal reflection",
    ],
    format: "Audio call or voice note",
    note: "Each booking is for one person and one type of spread. Book separately for additional people or spreads. Death, child gender, and gambling predictions are not offered. Calls may be ended without refund in cases of misbehaviour.",
    plans: [
      {
        title: "1 Question",
        duration: "Mini reading",
        price: 149,
        group: "By question",
      },
      {
        title: "2 Questions",
        duration: "Mini reading",
        price: 275,
        group: "By question",
      },
      {
        title: "3 Questions",
        duration: "Standard reading",
        price: 365,
        group: "By question",
      },
      {
        title: "4 Questions",
        duration: "Standard reading",
        price: 491,
        group: "By question",
      },
      {
        title: "5 Questions + Angel Guidance",
        duration: "In-depth reading",
        price: 581,
        group: "By question",
      },
      {
        title: "5-Minute Session",
        duration: "Mini reading",
        price: 159,
        group: "By time",
      },
      {
        title: "10-Minute Session",
        duration: "Mini reading",
        price: 273,
        group: "By time",
      },
      {
        title: "15-Minute Session",
        duration: "Standard reading",
        price: 392,
        group: "By time",
      },
      {
        title: "30-Minute Session",
        duration: "Standard reading",
        price: 699,
        group: "By time",
      },
      {
        title: "45-Minute Session",
        duration: "In-depth reading",
        price: 995,
        group: "By time",
      },
    ],
  },
  {
    slug: "reiki",
    title: "Reiki Healing",
    category: "Healing",
    image: reiki,
    imageAlt: "Crystals, a candle and oils arranged for a Reiki practice",
    intro: "A quiet pause. Space to reconnect.",
    description:
      "A gentle energy practice centred on relaxation, reflection, and balance. Choose a single Reiki session or a series of sessions to support your personal practice.",
    focus: ["Rest & relaxation", "Energy balance", "Emotional reflection"],
    format: "Individual healing session",
    plans: [
      { title: "Single Session", duration: "30 minutes", price: 799 },
      { title: "Single Session", duration: "60 minutes", price: 1399 },
      { title: "Healing Package", duration: "5 sessions", price: 5999 },
    ],
  },
  {
    slug: "relationship-healing",
    title: "Relationship Healing",
    category: "Counselling",
    image: relationship,
    imageAlt: "Illustration of two people reconnecting in a calm setting",
    intro: "Find a way back to understanding.",
    description:
      "A space to talk about emotional distance, recurring disagreements, and trust. Attend individually or together, with reflection practices and communication prompts to take into everyday life.",
    focus: ["Communication", "Trust & connection", "Understanding patterns"],
    format: "Individual or couple counselling",
    plans: [
      {
        title: "Single Person Counselling",
        duration: "45 minutes",
        price: 1199,
        note: "Explore your own needs and communication patterns.",
      },
      {
        title: "Couple Counselling",
        duration: "60 minutes",
        price: 1799,
        note: "For two partners who want to work together.",
      },
      {
        title: "Relationship Reset Package",
        duration: "3 sessions · 60 minutes each",
        price: 4999,
        note: "Continued support and practical reflection between sessions.",
      },
    ],
  },
  {
    slug: "hanuman-healing",
    title: "Panchamukhi Hanuman Ji Healing",
    category: "Healing",
    image: hanuman,
    imageAlt: "Traditional artwork depicting Hanuman Ji",
    intro: "Rooted in devotion. Guided by strength.",
    description:
      "A devotional healing practice inspired by Panchamukhi Hanuman Ji, with a focus on spiritual protection, grounding, and inner strength.",
    focus: ["Devotional practice", "Spiritual protection", "Grounding"],
    format: "Individual healing session",
    plans: sacredPlans("Deep Healing Package (For one person)"),
  },
  {
    slug: "maha-kali-healing",
    title: "Mahakali Healing",
    category: "Healing",
    image: kali,
    imageAlt: "Red devotional motif used for Mahakali healing",
    intro: "Reflect, release, and begin again.",
    description:
      "A spiritual practice dedicated to Maa Kali, offering space to reflect on repeating patterns and explore emotional shadow work through guided ritual.",
    focus: ["Shadow work", "Personal reflection", "Spiritual protection"],
    format: "Individual healing session",
    plans: sacredPlans("Deep Shadow Healing Package"),
  },
  {
    slug: "maa-kamakhya-healing",
    title: "Maa Kamakhya Healing",
    category: "Healing",
    image: kamakhya,
    imageAlt: "A lotus and lamps in a devotional temple setting",
    intro: "Connect with the sacred feminine.",
    description:
      "A devotional healing offering dedicated to Maa Kamakhya. Sessions focus on Shakti, emotional reflection, and intention, with preparation guidance shared before you begin.",
    focus: ["Shakti practice", "Emotional reflection", "Intention setting"],
    format: "Individual healing session",
    plans: sacredPlans("Deep Shakti Healing Package"),
  },
  {
    slug: "tarot-spells",
    title: "Tarot Rituals & Spells",
    category: "Guidance",
    image: ritual,
    imageAlt: "Flowers, a candle and crystals arranged for a ritual",
    intro: "Give your intention a place to begin.",
    description:
      "Explore tarot-inspired rituals for love, work, prosperity, protection, or self-empowerment. Choose an intention and a duration, or discuss a personalised ritual with Vaanii.",
    focus: ["Personal intention", "Guided ritual", "Spiritual reflection"],
    format: "1-day, 7-day or custom ritual",
    plans: [],
  },
  {
    slug: "past-life",
    title: "Past Life Regression",
    category: "Guidance",
    image: temple,
    imageAlt: "Stone pillars lining a quiet temple corridor",
    intro: "Explore the stories you carry.",
    description:
      "A guided exploration of imagery, recurring themes, and personal meaning through relaxation. Start with a counselling session to discuss the process, your expectations, and suitability.",
    focus: ["Guided reflection", "Recurring themes", "Personal meaning"],
    format: "Counselling, guided or interactive session",
    note: "Download and complete the consent form before booking. Discuss preparation and any concerns with Vaanii before choosing a session.",
    plans: [
      { title: "Counseling Session", duration: "15–30 minutes", price: 491 },
      { title: "Guided Session", duration: "60–90 minutes", price: 1551 },
      { title: "Interactive Session", duration: "60–90 minutes", price: 1551 },
    ],
  },
  {
    slug: "mobile-number-correction",
    title: "Mobile Number Analysis",
    paymentName: "Phone Number Numerology",
    category: "Guidance",
    image: numbers,
    imageAlt: "Hands holding a collection of small numbered objects",
    intro: "A fresh look at your everyday numbers.",
    description:
      "Explore your phone number through the lens of numerology, with an analysis of its relationship to your date of birth. Choose a report for one number or compare several.",
    focus: ["Number patterns", "Birth date alignment", "Personal report"],
    format: "Detailed numerology report",
    plans: [
      {
        title: "Phone Number Analysis Report",
        duration: "1 phone number",
        price: 699,
      },
      {
        title: "Phone Number Analysis Report - Bundle for 2",
        duration: "2 personal phone numbers",
        price: 1331,
      },
      {
        title: "Phone Number Analysis Report - Bundle for 4",
        duration: "4 phone numbers",
        price: 2556,
      },
    ],
  },
  {
    slug: "therapy",
    title: "Counselling & Therapy",
    category: "Counselling",
    image: therapy,
    imageAlt: "An illustration of two overlapping profiles with a heart",
    intro: "A conversation that starts with you.",
    description:
      "Make time to talk about what you are experiencing in a private, supportive space. Explore your emotions, everyday challenges, and the next steps that feel right for you.",
    focus: [
      "Emotional well-being",
      "Personal challenges",
      "Self-understanding",
    ],
    format: "Private counselling session",
    plans: [
      { title: "Counselling Session", duration: "30 minutes", price: 699 },
      { title: "Counselling Session", duration: "60 minutes", price: 1299 },
      {
        title: "Therapy Package",
        duration: "3 sessions · 30 minutes each",
        price: 2100,
      },
      {
        title: "Therapy Package",
        duration: "5 sessions · 45 minutes each",
        price: 4500,
      },
    ],
  },
  {
    slug: "courses/tarot",
    title: "Tarot Mastery Course",
    category: "Courses",
    image: cards,
    imageAlt: "A set of illustrated tarot cards with crystals",
    intro: "From curiosity to your own practice.",
    description:
      "Build a tarot practice across a 16-day curriculum. Learn the cards, work with spreads, and practise readings. Choose live Google Meet classes or recorded lessons that fit your schedule.",
    focus: [
      "16-day curriculum",
      "Live or recorded",
      "Certificate on completion",
    ],
    format: "Live Google Meet or recorded lessons",
    plans: [
      {
        title: "Live Sessions",
        duration: "16-day course · interactive classes",
        price: 7999,
      },
      {
        title: "Recorded Sessions",
        duration: "Self-paced lessons",
        price: 5999,
      },
    ],
    curriculum: [
      "Introduction to Tarot",
      "Cleansing, charging, and connecting",
      "The Major Arcana · part one",
      "The Major Arcana · part two",
      "The Suit of Wands",
      "The Suit of Cups",
      "The Suit of Swords",
      "Pentacles & the Court Cards",
      "Tarot with numerology, astrology & Vastu",
      "Spreads, time frames & Mahoorat",
      "Tarot spells & name initials",
      "Cord cutting & designing your own spreads",
      "Live reading practice · part one",
      "Live reading practice · part two",
      "Live reading practice · part three",
      "Starting your tarot business & feedback",
    ],
  },
  {
    slug: "courses/healing-oils-course",
    title: "Healing Oils Course",
    paymentName: "Magical Oils Course",
    category: "Courses",
    image: oils,
    imageAlt: "Bottles, herbs and a mortar on a sunlit table",
    intro: "Make ritual part of your everyday.",
    description:
      "A two-day course in preparing oils, sprays, and personal rituals. Explore ingredients, traditional uses, and practical preparation across nine modules.",
    focus: ["2-day course", "9 practical modules", "Certificate on completion"],
    format: "Two-day certificate course",
    plans: [
      { title: "Full Course", duration: "2 days · 9 modules", price: 999 },
    ],
    curriculum: [
      "Foundations: base oils, essential oils, herbs, spices & crystals",
      "Traditional oils for prosperity & career intentions",
      "Traditional oils for protection & obstacles",
      "Oils in relationship & fertility rituals",
      "Oils for well-being, meditation & study",
      "Crafting sprays for personal and home rituals",
      "Rituals for financial intentions",
      "Rituals for well-being, love & intuition",
      "Numerology baths, oil application & questions",
    ],
  },
  {
    slug: "numerology",
    title: "Numerology",
    category: "Guidance",
    image: numbers,
    imageAlt: "A collection of numbers resting in open hands",
    intro: "There is more to explore.",
    description:
      "Life path analysis, name numerology, compatibility readings, and personal year forecasts are being prepared. Mobile number analysis is available now.",
    focus: ["Life path", "Name numerology", "Compatibility"],
    format: "Coming soon",
    plans: [],
    comingSoon: true,
  },
];
export const ritualGroups = [
  {
    title: "Love & Relationship Spells",
    price: 896,
    items: [
      "Attracting new love",
      "Strengthening bond",
      "Healing heartbreak",
      "Personalised Rituals",
    ],
  },
  {
    title: "Career & Success Spells",
    price: 932,
    items: [
      "Job opportunities",
      "Removing obstacles",
      "Confidence & recognition",
      "Business growth",
      "Personalised Rituals",
    ],
  },
  {
    title: "Prosperity & Money Spells",
    price: 959,
    items: [
      "Financial stability",
      "Luck & opportunities",
      "Clearing blockages",
      "Personalised Rituals",
    ],
  },
  {
    title: "Protection & Cleansing Spells",
    price: 887,
    items: [
      "Negativity removal",
      "Evil eye / jealousy protection",
      "Space & aura cleansing",
      "Personalised Rituals",
    ],
  },
  {
    title: "Healing & Self-Empowerment Spells",
    price: 851,
    items: [
      "Emotional healing",
      "Inner strength & courage",
      "Anxiety / stress relief",
      "Self-love & confidence",
      "Personalised Rituals",
    ],
  },
];
export const servicePath = (service: Service) => `/services/${service.slug}`;
export const findService = (slug: string) =>
  services.find((s) => s.slug === slug)!;
